import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { AccountErrorCodes, ProfileErrorCodes } from '@app/translations';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  ButtonActionType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { UpdateFormDataActionWithId } from '../../../actions/form-submission.action';
import { ConfirmationHelperService } from '../../../services/confirmation-helper/confirmation-helper.service';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { handleRedirection } from '../cta-redirection.utils';
import { FormActionState } from '../../../state/form-submision.state';
import { ProjectSettingsState } from '../../../state/project-settings.state';
import { buildNestedPatch, getNestedValue } from '../../../utils/nested-object-path.utils';

/**
 * What the server falls back to when a project publishes no `verified_contact_cache_ttl_minutes`
 * (see `OtpVerificationService.storeVerifiedContact`). Mirrored here so a missing TTL narrows the
 * client's expiry check to match the server rather than disabling it.
 */
export const DEFAULT_VERIFIED_CONTACT_TTL_MINUTES = 30;

export type ValidateOnboardingOtpExpiryOptions = {
  formId: string;
  widgetProperties: BaseWidgetProperties;
  injector: Injector;
  submit: () => void;
  /**
   * When true, also validate the COP (Check Payee) verification timestamp.
   * Defaults to false because the business onboarding flow has no COP step.
   */
  includeCop?: boolean;
  /**
   * Set only on the path taken after the server rejected a submit with
   * PROFILE_OTP_VERIFICATION_REQUIRED. If the client still believes both channels are fresh, its
   * view disagrees with the server's and re-submitting would loop forever — so this path never
   * submits again.
   */
  isRetryAfterServerRejection?: boolean;
  /**
   * The action button type that triggered the expiry check. When provided, the expiry handlers can
   * fall back to the default navigation config for that action if the widget has no outcome
   * navigation configured, instead of silently redirecting to an unrelated zone.
   */
  buttonActionType?: ButtonActionType;
};

/**
 * Resolves a configured TTL, falling back to the server default. Truthiness (not `?? `) on purpose:
 * the server only honours a truthy TTL, so `0`/`null` must land on the same 30-minute default here.
 */
export function resolveVerifiedContactTtlMinutes(configured: number | undefined): number {
  return configured ? configured : DEFAULT_VERIFIED_CONTACT_TTL_MINUTES;
}

// Returns true if the verification has expired.
// A missing/invalid `verifiedAt` timestamp is treated as expired.
// `expiryInMinutes` is the duration (in minutes) the verification stays valid for.
export function isVerificationExpired(
  verifiedAt: string | undefined,
  expiryInMinutes: string | number | undefined,
  currentTime: number,
): boolean {
  if (!verifiedAt) {
    return true;
  }
  const verifiedTime = new Date(verifiedAt).getTime();
  if (Number.isNaN(verifiedTime)) {
    return true;
  }
  const expiryMinutes = Number(expiryInMinutes);
  if (Number.isNaN(expiryMinutes)) {
    return false;
  }
  return currentTime - verifiedTime > expiryMinutes * 60 * 1000;
}

function buildVerificationClearPayload(
  store: Store,
  formId: string,
  updatedValues: Record<string, unknown>,
): Record<string, unknown> {
  const currentFormData = store.selectSnapshot(FormActionState.getFormActionStateWithId(formId))?.formData;
  const primaryOfficer = getNestedValue<Record<string, unknown>>(currentFormData, 'primaryOfficer');
  if (!primaryOfficer) return updatedValues;

  const clearedPrimaryOfficer = { ...primaryOfficer, ...updatedValues };
  return {
    ...buildNestedPatch(currentFormData as Record<string, unknown>, 'primaryOfficer', clearedPrimaryOfficer),
    ...updatedValues,
  };
}

export function validateOnboardingOtpExpiry(options: ValidateOnboardingOtpExpiryOptions): void {
  const {
    formId,
    widgetProperties,
    injector,
    submit,
    includeCop = false,
    isRetryAfterServerRejection = false,
    buttonActionType,
  } = options;

  const store = injector.get(Store);
  const projectSettings = store.selectSnapshot(ProjectSettingsState.getProjectSettings);
  const emailOtpExpiryTime = resolveVerifiedContactTtlMinutes(
    projectSettings.projectConfig?.email_config?.verified_contact_cache_ttl_minutes,
  );
  const phoneOtpExpiryTime = resolveVerifiedContactTtlMinutes(
    projectSettings.projectConfig?.sms_config?.verified_contact_cache_ttl_minutes,
  );
  const formData = store.selectSnapshot(FormActionState.getFormActionStateWithId(formId))?.formData;
  // Business onboarding collects the primary contact inside a nested primaryOfficer group. The
  // inline input widget and the OTP page can write markers at either the top level or inside that
  // group, so prefer the group when it carries a timestamp - it is the source of truth for the
  // contact the form is actually bound to.
  const primaryOfficer = formData?.['primaryOfficer'] as Record<string, unknown> | undefined;
  const emailVerifiedAt = (primaryOfficer?.['emailVerifiedAt'] ?? formData?.['emailVerifiedAt']) as
    string | undefined;
  const phoneVerifiedAt = (primaryOfficer?.['phoneVerifiedAt'] ?? formData?.['phoneVerifiedAt']) as
    string | undefined;
  const currentTime = Date.now();

  const isEmailOtpExpired = isVerificationExpired(emailVerifiedAt, emailOtpExpiryTime, currentTime);
  const isPhoneOtpExpired = isVerificationExpired(phoneVerifiedAt, phoneOtpExpiryTime, currentTime);

  if (isPhoneOtpExpired) {
    handlePhoneOtpExpiryForForm(formId, widgetProperties, injector, buttonActionType);
    return;
  }

  if (isEmailOtpExpired) {
    handleEmailOtpExpiryForForm(formId, widgetProperties, injector, buttonActionType);
    return;
  }

  if (includeCop && formData?.bankDetailsVerificationTokens) {
    const copCheckExpiryTime =
      projectSettings.projectConfig?.checkPayeeConfig?.checkPayeeVerifiedCacheTtlMinutes;
    if (copCheckExpiryTime) {
      const isCopExpired = isVerificationExpired(formData?.['copVerifiedAt'], copCheckExpiryTime, currentTime);
      if (isCopExpired) {
        handleCopExpiryForForm(formId, widgetProperties, injector, buttonActionType);
        return;
      }
    }
  }

  if (isRetryAfterServerRejection) {
    // Nothing looks expired from here, yet the server refused the verification — the client's
    // timestamps are not the authority, the server's cache entry and token are. Submitting again
    // would produce the same 403 indefinitely.
    handleServerRejectedVerificationForForm(formId, widgetProperties, injector, buttonActionType);
    return;
  }

  submit();
}

export function handleEmailOtpExpiryForForm(
  formId: string,
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
  buttonActionType?: ButtonActionType,
) {
  const workflowFormsService = injector.get(WorkflowFormsService);
  const confirmationHelperService = injector.get(ConfirmationHelperService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const store = injector.get(Store);
  const router = injector.get(Router);

  const formPart = workflowFormsService.getFormPart(formId);
  const updatedValues = {
    isEmailVerified: false,
    emailVerifiedAt: null,
    verifiedEmail: null,
    emailVerificationToken: null,
  };
  formPart?.patchValue(updatedValues, { emitEvent: false });
  store.dispatch(
    new UpdateFormDataActionWithId(buildVerificationClearPayload(store, formId, updatedValues), formId),
  );
  const outcomeType = ActionButtonOutcomesType.ALTERNATE_2;
  if (!widgetProperties.actionNavigationConfigurations?.[outcomeType] && !buttonActionType) {
    findAndShowAlertFromCode(store, router, alertHandlerService, [
      ProfileErrorCodes.PROFILE_OTP_VERIFICATION_REQUIRED,
    ]);
    return;
  }
  confirmationHelperService.setReconfirmationConfig({
    redirectUrl: router.url,
    reconfirmationType: 'email',
  });
  handleRedirection(widgetProperties, injector, outcomeType, true, true, buttonActionType);
}

export function handlePhoneOtpExpiryForForm(
  formId: string,
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
  buttonActionType?: ButtonActionType,
) {
  const workflowFormsService = injector.get(WorkflowFormsService);
  const confirmationHelperService = injector.get(ConfirmationHelperService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const store = injector.get(Store);
  const router = injector.get(Router);

  const formPart = workflowFormsService.getFormPart(formId);
  const updatedValues = {
    isPhoneVerified: false,
    phoneVerifiedAt: null,
    verifiedPhone: null,
    verifiedCountryCode: null,
    phoneVerificationToken: null,
  };
  formPart?.patchValue(updatedValues, { emitEvent: false });
  store.dispatch(
    new UpdateFormDataActionWithId(buildVerificationClearPayload(store, formId, updatedValues), formId),
  );
  const outcomeType = ActionButtonOutcomesType.ALTERNATE_2;
  if (!widgetProperties.actionNavigationConfigurations?.[outcomeType] && !buttonActionType) {
    findAndShowAlertFromCode(store, router, alertHandlerService, [
      ProfileErrorCodes.PROFILE_OTP_VERIFICATION_REQUIRED,
    ]);
    return;
  }
  confirmationHelperService.setReconfirmationConfig({
    redirectUrl: router.url,
    reconfirmationType: 'phone',
  });
  handleRedirection(widgetProperties, injector, outcomeType, true, true, buttonActionType);
}

/**
 * The server rejected the verification while the client still considered both channels fresh. We
 * cannot tell which channel it objected to, so both are cleared: that is the only choice that
 * cannot leave the user submitting a verification the server will not accept, and it restores the
 * verify buttons so there is a way forward.
 */
export function handleServerRejectedVerificationForForm(
  formId: string,
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
  buttonActionType?: ButtonActionType,
) {
  const workflowFormsService = injector.get(WorkflowFormsService);
  const confirmationHelperService = injector.get(ConfirmationHelperService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const store = injector.get(Store);
  const router = injector.get(Router);

  const formPart = workflowFormsService.getFormPart(formId);
  const updatedValues = {
    isEmailVerified: false,
    emailVerifiedAt: null,
    verifiedEmail: null,
    emailVerificationToken: null,
    isPhoneVerified: false,
    phoneVerifiedAt: null,
    verifiedPhone: null,
    verifiedCountryCode: null,
    phoneVerificationToken: null,
  };
  formPart?.patchValue(updatedValues, { emitEvent: false });
  store.dispatch(
    new UpdateFormDataActionWithId(buildVerificationClearPayload(store, formId, updatedValues), formId),
  );

  const outcomeType = ActionButtonOutcomesType.ALTERNATE_2;
  if (!widgetProperties.actionNavigationConfigurations?.[outcomeType] && !buttonActionType) {
    findAndShowAlertFromCode(store, router, alertHandlerService, [
      ProfileErrorCodes.PROFILE_OTP_VERIFICATION_REQUIRED,
    ]);
    return;
  }
  confirmationHelperService.setReconfirmationConfig({
    redirectUrl: router.url,
    reconfirmationType: 'phone',
  });
  handleRedirection(widgetProperties, injector, outcomeType, true, true, buttonActionType);
}

export function handleCopExpiryForForm(
  formId: string,
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
  buttonActionType?: ButtonActionType,
) {
  const store = injector.get(Store);
  const confirmationHelperService = injector.get(ConfirmationHelperService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const router = injector.get(Router);
  store.dispatch(
    new UpdateFormDataActionWithId(
      {
        bankDetailsVerificationTokens: [],
        copVerifiedAt: null,
      },
      formId,
    ),
  );
  if (
    !widgetProperties.actionNavigationConfigurations?.[ActionButtonOutcomesType.ALTERNATE_3] &&
    !buttonActionType
  ) {
    findAndShowAlertFromCode(store, router, alertHandlerService, [
      AccountErrorCodes.COP_CHECK_VERIFICATION_EXPIRED,
    ]);
    return;
  }
  confirmationHelperService.setReconfirmationConfig({
    redirectUrl: router.url,
    reconfirmationType: 'cop',
  });
  handleRedirection(
    widgetProperties,
    injector,
    ActionButtonOutcomesType.ALTERNATE_3,
    true,
    true,
    buttonActionType,
  );
}
